/**
 * Symptom definitions. FR-204.
 *
 * The built-in catalogue is the list from the elicitation document. Every label
 * is a copy-layer key rather than literal text, so a future locale translates
 * them (ADR-0005) — user-created symptoms will carry literal text instead,
 * because someone's own words must never be replaced by a translation.
 *
 * Definitions live here and NOT inside a day entry (data model Section 2):
 * renaming or hiding a symptom must not rewrite ten years of records. Entries
 * reference ids.
 */
import type { SymptomId } from '@domain/types'
import { openDb, promisify, STORE, txDone } from './db'

export interface SymptomDef {
  id: SymptomId
  /** Copy-layer key for built-ins. */
  labelKey: string
  /** User-authored text, for symptoms added by the user. Not translated. */
  label?: string
  category: 'physical' | 'mental' | 'energy' | 'other'
  builtIn: boolean
  hidden: boolean
  order: number
}

const builtIn = (
  id: string,
  category: SymptomDef['category'],
  order: number,
): SymptomDef => ({
  id,
  labelKey: `symptom.${id}`,
  category,
  builtIn: true,
  hidden: false,
  order,
})

/**
 * Seeded on first run. Deliberately not exhaustive — the point of custom fields
 * (FR-212, v1.1) is that no list could be. These are the ones common enough to
 * be worth a tap rather than a setup step.
 */
export const DEFAULT_SYMPTOMS: readonly SymptomDef[] = [
  builtIn('cramps', 'physical', 10),
  builtIn('headache', 'physical', 20),
  builtIn('bloating', 'physical', 30),
  builtIn('breastTenderness', 'physical', 40),
  builtIn('acne', 'physical', 50),
  builtIn('nausea', 'physical', 60),
  builtIn('fatigue', 'physical', 70),
  builtIn('backPain', 'physical', 80),
  builtIn('weakness', 'physical', 90),
  builtIn('anxiety', 'mental', 110),
  builtIn('lowMood', 'mental', 120),
  builtIn('irritability', 'mental', 130),
  builtIn('stress', 'mental', 140),
  builtIn('poorFocus', 'mental', 150),
  builtIn('goodMood', 'mental', 160),
  builtIn('calm', 'mental', 170),
  builtIn('focused', 'mental', 180),
  builtIn('energetic', 'mental', 190),
]

export async function loadSymptoms(): Promise<SymptomDef[]> {
  const db = await openDb()
  const tx = db.transaction(STORE.symptoms, 'readonly')
  const rows = await promisify<SymptomDef[]>(tx.objectStore(STORE.symptoms).getAll())

  if (rows.length === 0) {
    await seedSymptoms()
    return [...DEFAULT_SYMPTOMS]
  }

  // Seed default symptoms that are missing from an already-initialized database
  const missing = DEFAULT_SYMPTOMS.filter((d) => !rows.some((r) => r.id === d.id))
  if (missing.length > 0) {
    const writeTx = db.transaction(STORE.symptoms, 'readwrite')
    const store = writeTx.objectStore(STORE.symptoms)
    for (const def of missing) {
      store.put(def)
      rows.push(def)
    }
    await txDone(writeTx)
  }

  return rows.sort((a, b) => a.order - b.order)
}

async function seedSymptoms(): Promise<void> {
  const db = await openDb()
  const tx = db.transaction(STORE.symptoms, 'readwrite')
  const store = tx.objectStore(STORE.symptoms)
  for (const def of DEFAULT_SYMPTOMS) store.put(def)
  await txDone(tx)
}

export async function putSymptom(def: SymptomDef): Promise<void> {
  const db = await openDb()
  const tx = db.transaction(STORE.symptoms, 'readwrite')
  tx.objectStore(STORE.symptoms).put(def)
  await txDone(tx)
}

/**
 * Hide rather than delete, always.
 *
 * Historical entries reference the id. Deleting the definition would orphan
 * real data the user recorded — see `CustomFieldDef.archived` in data model
 * Section 2 for the same reasoning.
 */
export async function hideSymptom(id: SymptomId, hidden = true): Promise<void> {
  const db = await openDb()
  const tx = db.transaction(STORE.symptoms, 'readwrite')
  const store = tx.objectStore(STORE.symptoms)
  const existing = await promisify<SymptomDef | undefined>(store.get(id))
  if (existing) store.put({ ...existing, hidden })
  await txDone(tx)
}
